import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchBalance } from '@/features/dashboard/balanceApi'
import { queryKeys } from '@/shared/api/queryKeys'
import { setCurrencyCode, setDisplayTimeZone } from '@/shared/lib/format'
import { AuthContext, type AuthContextValue } from './AuthContext'
import { fetchMe, login, logout, type CurrentUser } from './authApi'
import { clearSession, hasValidToken, saveSession } from './tokenStorage'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Token localStorage'da, lekin React uni "ko'rmaydi" — shuning uchun
  // holatga nusxalanadi. Aks holda kirgandan keyin daraxt qayta chizilmaydi.
  const [hasToken, setHasToken] = useState(hasValidToken)

  const meQuery = useQuery<CurrentUser>({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    enabled: hasToken,
    // Kim ekanimiz sessiya davomida o'zgarmaydi
    staleTime: Infinity,
    retry: false,
  })

  /**
   * ⚠️ VALYUTA ENDI `/me` DAN EMAS — HAQIQIY backend `MeResponse`da
   * `currency` maydoni umuman yo'q (`authApi.ts` dagi izoh). Yagona manba
   * `GET /api/selfcare/balance` javobidagi `money.currency`, shuning uchun
   * bu so'rov shu yerda, ILDIZDA, `me` bilan BIRGA so'raladi — faqat
   * `DashboardPage`/`PaymentsPage` ichida emas.
   *
   * TanStack Query keshi tufayli bu ikki karra tarmoq so'rovi degani EMAS:
   * `queryKeys.balance` bitta, `staleTime` (30s, `queryClient.ts`) ichida
   * Dashboard/Payments o'zi qayta so'ramay shu keshni ishlatadi.
   */
  const balanceQuery = useQuery({
    queryKey: queryKeys.balance,
    queryFn: fetchBalance,
    enabled: hasToken,
    retry: false,
  })

  const user = meQuery.data ?? null

  /**
   * TENANT VALYUTASI SHU YERDA O'RNATILADI — admin panelidagi bilan bir xil
   * muhim tamoyil.
   *
   * `formatMoney` valyutani modul darajasidagi o'zgaruvchidan o'qiydi
   * (`shared/lib/format.ts`) — u REAKTIV EMAS. Agar bu qiymat daraxtning
   * pastida o'rnatilsa, uni so'ramagan ekranlarda summalar ZAXIRA valyutada
   * chiqadi. Yechim ikki qismdan iborat va IKKALASI ham kerak:
   *   1. qiymat ILDIZDA, `balance` javobidan o'rnatiladi (shu yer)
   *   2. `balance` yuklanmaguncha himoyalangan ekranlar CHIZILMAYDI
   *      (`RequireAuth`) — aks holda summalar avval noto'g'ri chiqib,
   *      keyin sakrab o'zgaradi
   *
   * NEGA `useEffect` EMAS: effekt birinchi chizilishdan KEYIN ishlaydi, ya'ni
   * bolalar bir marta eski valyuta bilan chizilib ulgurardi.
   */
  if (balanceQuery.data) {
    setCurrencyCode(balanceQuery.data.money.currency)
  }

  /**
   * TENANT VAQT ZONASI — valyuta bilan AYNAN bir xil sabab bo'yicha shu
   * yerda, ILDIZDA o'rnatiladi: `formatDateTime` zonani modul darajasidagi
   * o'zgaruvchidan o'qiydi va u REAKTIV EMAS.
   *
   * NEGA `useEffect` EMAS: effekt birinchi chizilishdan KEYIN ishlaydi —
   * jadvaldagi sanalar bir marta BRAUZER zonasida chizilib, keyin
   * sakrab o'zgarardi. Kun chegarasi tenant zonasida hisoblangani uchun
   * bu sakrash sanani ham surib qo'yishi mumkin edi.
   *
   * Server bu maydonni bermasa (eski versiya) — `setDisplayTimeZone` uni
   * jimgina rad etadi va brauzer zonasi ishlatiladi.
   */
  if (user) {
    setDisplayTimeZone(user.dayTimezone)
  }

  const signIn = useCallback(
    async (loginValue: string, password: string) => {
      const result = await login({ login: loginValue, password })
      saveSession(result.token, result.expiresAt)
      setHasToken(true)
      // `login` javobida `me`/`balance` yo'q (kontrakt bo'yicha faqat token) —
      // shuning uchun keshni tozalab, so'rovlarni qaytadan ishga tushiramiz
      queryClient.removeQueries({ queryKey: queryKeys.me })
      queryClient.removeQueries({ queryKey: queryKeys.balance })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.balance }),
      ])
    },
    [queryClient],
  )

  const signOut = useCallback(async () => {
    await logout()
    clearSession()
    setHasToken(false)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: hasToken && user !== null,
      // Ikkalasi ham (me VA balance) yuklanguncha kutamiz — ikkalasi ham
      // himoyalangan ekranlar to'g'ri ishlashi uchun kerak (rol/holat va valyuta)
      isLoading: hasToken && (meQuery.isPending || balanceQuery.isPending),
      error: meQuery.error ?? balanceQuery.error,
      signIn,
      signOut,
    }),
    [
      user,
      hasToken,
      meQuery.isPending,
      meQuery.error,
      balanceQuery.isPending,
      balanceQuery.error,
      signIn,
      signOut,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
